// api/clients.js - Fixed version with proper authentication
import axios from "axios";

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || "http://localhost:8000/api",
  timeout: 30000, // Increased timeout for file downloads
  headers: {
    Accept: "application/json",
  },
});

// Request interceptor to add authentication token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("jobToken");

    // Only add auth token if the request explicitly requires it
    // You can set this in individual requests: { requiresAuth: true }
    if (token && config.requiresAuth !== false) {
      // Based on your API's WWW-Authenticate: Bearer header, use Bearer format
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Special handling for file downloads
    if (config.responseType === "blob") {
      config.headers.Accept =
        "application/zip, application/octet-stream, application/pdf, */*";
      config.timeout = 300000; // 5 minutes for large file downloads
    }

    console.log("API Request:", {
      url: config.url,
      method: config.method,
      headers: config.headers,
      params: config.params,
      data: config.data,
      requiresAuth: config.requiresAuth,
      hasAuth: !!config.headers.Authorization,
    });

    return config;
  },
  (error) => {
    console.error("Request interceptor error:", error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle authentication errors
apiClient.interceptors.response.use(
  (response) => {
    console.log("API Response:", {
      url: response.config.url,
      status: response.status,
      contentType: response.headers["content-type"],
      dataType: typeof response.data,
      hasData: !!response.data,
    });
    return response;
  },
  (error) => {
    console.error("API Error:", {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      data: error.response?.data,
      message: error.message,
      hasAuth: !!error.config?.headers?.Authorization,
    });

    // Handle 401 errors globally
    if (error.response?.status === 401) {
      if (
        error.response.data.message ===
        "Invalid credentials. Please check your email and password."
      ) {
        console.log("Invalid credentials");
      } else {
        console.log("401 error detected, clearing auth data");
        localStorage.removeItem("jobToken");
        localStorage.removeItem("jobUserId");
        localStorage.removeItem("jobRefreshToken");

        // Optional: Redirect to login
        window.location.href = "/jobs/job-auth";
      }
    }

    return Promise.reject(error);
  }
);

// Helper functions for different types of requests
export const publicRequest = {
  get: (url, config = {}) =>
    apiClient({ ...config, method: "GET", url, requiresAuth: false }),
  post: (url, data, config = {}) =>
    apiClient({ ...config, method: "POST", url, data, requiresAuth: false }),
  put: (url, data, config = {}) =>
    apiClient({ ...config, method: "PUT", url, data, requiresAuth: false }),
  patch: (url, data, config = {}) =>
    apiClient({ ...config, method: "PATCH", url, data, requiresAuth: false }),
  delete: (url, config = {}) =>
    apiClient({ ...config, method: "DELETE", url, requiresAuth: false }),
  request: (config) => apiClient({ ...config, requiresAuth: false }),
};

export const authenticatedRequest = {
  get: (url, config = {}) =>
    apiClient({ ...config, method: "GET", url, requiresAuth: true }),
  post: (url, data, config = {}) =>
    apiClient({ ...config, method: "POST", url, data, requiresAuth: true }),
  put: (url, data, config = {}) =>
    apiClient({ ...config, method: "PUT", url, data, requiresAuth: true }),
  patch: (url, data, config = {}) =>
    apiClient({ ...config, method: "PATCH", url, data, requiresAuth: true }),
  delete: (url, config = {}) =>
    apiClient({ ...config, method: "DELETE", url, requiresAuth: true }),
  request: (config) => apiClient({ ...config, requiresAuth: true }),
};

export default apiClient;
